import PipiPupuAPIClient from '../PipiPupuAPIClient';
import ChatSocket from '../ChatSocket';
import SoundManager from '../SoundManager';
import VampiVoiceClient from '../VampiVoiceClient';
import MmlApiClient from '../MmlApiClient';
import { tryFetch } from '../widgetLoader';

// Progress-style fields the server keeps updating on the account-wide settings row (goal counters etc.).
// An overlay widget instance owns its own appearance settings but still follows these live.
const RUNTIME_FIELDS = {
	'gift-goal': [ 'amount', 'goal' ],
	'sub-goal': [ 'count', 'goal' ],
	'follower-goal': [ 'count', 'goal' ],
	'gift': [ 'stopped' ],
};

// Everything a page shares across all the widgets it hosts: one API client, one socket, one sound manager.
// `callContext` is only passed by the call studio editor/render/guest pages, for the camera/speaker/camera-grid
// widget mounters (see overlay/widgetMounters/camera.js) - absent, every other widget page ignores it.
export function createSharedServices({ hash, apiBase, socketBase, callContext }) {
	const api = new PipiPupuAPIClient(`${apiBase ?? process.env.VUE_APP_PIPIPUPUBASE ?? ''}/api`);
	const socket = new ChatSocket(`${socketBase ?? process.env.VUE_APP_PIPIPUPUSOCKETBASE}/api/socket`);
	const voiceApi = new VampiVoiceClient(process.env.VUE_APP_VAMPIVOICEAPIURL, process.env.VUE_APP_VAMPIVOICEAPIKEY);
	const mmlApi = new MmlApiClient(process.env.VUE_APP_VAMPIMMLAPIURL, process.env.VUE_APP_VAMPIMMLAPIKEY);
	let soundManager = null;
	return {
		hash,
		api,
		socket,
		voiceApi,
		mmlApi,
		callContext: callContext || null,
		baseUrl: process.env.VUE_APP_PIPIPUPUBASE || `${location.protocol}//${location.host}`,
		get soundManager() { return soundManager ||= new SoundManager({ voiceApi, mmlApi }); },
	};
}

class WidgetHost {
	constructor(shared, type, container) {
		Object.assign(this, { type, container, hash: shared.hash, api: shared.api, socket: shared.socket, voiceApi: shared.voiceApi, mmlApi: shared.mmlApi, baseUrl: shared.baseUrl, callContext: shared.callContext || null });
		this._shared = shared;
		this._subscriptions = [];
		this._timers = [];
		this._destroyers = [];
		this._settingsHandlers = [];
		this._fieldHandler = null;
		this.current = {};
		this.physics = null;
	}

	get soundManager() { return this._shared.soundManager; }

	tryFetch(path, cacheKey, transform) {
		return tryFetch(() => this.api.get(`widget/${encodeURIComponent(this.hash)}/${path}`), `cache-${this.hash}-${cacheKey}`, transform);
	}

	onSettingsChange(fn) { this._settingsHandlers.push(fn); }
	onFieldChange(fn) { this._fieldHandler = fn; }
	onDestroy(fn) { this._destroyers.push(fn); }

	every(fn, ms) {
		const id = setInterval(fn, ms);
		this._timers.push(id);
		return id;
	}

	_applySettings(settings) {
		this.current = settings;
		for(const fn of this._settingsHandlers) fn(settings);
	}

	_applyField(field, value) {
		if(this._fieldHandler) this._fieldHandler(field, value);
		else this._applySettings({ ...this.current, [field]: value });
	}

	destroy() {
		for(const sub of this._subscriptions) this.socket.unsubscribe(sub);
		for(const id of this._timers) clearInterval(id);
		for(const fn of this._destroyers) {
			try { fn(); } catch(e) { console.error('Widget destroy error', e); }
		}
		this._subscriptions = [];
		this._timers = [];
		this._destroyers = [];
	}
}

// A widget instance inside an overlay layer: its own settings, seeded by the layer, plus live runtime state.
export default class OverlayHost extends WidgetHost {
	constructor(shared, type, container, settings) {
		super(shared, type, container);
		this.layerSettings = settings || {};
		this.runtime = {};
	}

	async loadSettings() {
		const fields = RUNTIME_FIELDS[this.type];
		if(fields) {
			const global = await this.tryFetch(`settings/${this.type}`, `${this.type}-settings`) || {};
			for(const f of fields) if(f in global) this.runtime[f] = global[f];
		}
		this.current = { ...this.layerSettings, ...this.runtime };
		return this.current;
	}

	// Called by the overlay page when this layer's own settings were edited.
	pushSettings(settings) {
		this.layerSettings = settings || {};
		this._applySettings({ ...this.layerSettings, ...this.runtime });
	}

	subscribe(handlers) {
		this._subscriptions.push(this.socket.subscribe(this.hash, {
			...handlers,
			widgetSettingsFieldChange: msg => {
				if(msg.widgetType !== this.type || !RUNTIME_FIELDS[this.type]?.includes(msg.field)) return;
				this.runtime[msg.field] = msg.value;
				this._applyField(msg.field, msg.value);
			},
		}));
	}
}
